import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  ChevronLeft, Save, Loader2, ChevronDown, ChevronUp, Upload, Pencil,
  Plus, X, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";

const inputCls = "bg-[#0b0811] border border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935]";
const PROGRAM_COLORS = ["#3b82f6", "#f59e0b", "#ef4444", "#84cc16", "#a855f7", "#06b6d4", "#ec4899", "#14b8a6"];

const SECTIONS = [
  { k: "modalites", l: "Modalites", type: "text" },
  { k: "type_action", l: "Type d'action de formation", type: "select", options: ["Formation", "Bilan de competences", "VAE", "Apprentissage"] },
  { k: "description", l: "Description du programme", type: "textarea" },
  { k: "objectifs_pedagogiques", l: "Objectifs pedagogiques", type: "list" },
  { k: "profil_apprenants", l: "Profil des apprenants", type: "textarea" },
  { k: "contenu_pedagogique", l: "Contenu de la formation (progression pedagogique)", type: "textarea" },
  { k: "suivi_execution", l: "Suivi de l'execution", type: "textarea" },
  { k: "modalites_certification", l: "Modalites de certification", type: "textarea" },
  { k: "qualite", l: "Qualite", type: "textarea" },
  { k: "config_comptable", l: "Configuration comptable du produit", type: "text" },
  { k: "domaines_competences", l: "Domaines de competences", type: "list" },
];

export default function ProgramEditPage() {
  const { program_id } = useParams();
  const navigate = useNavigate();
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [prog, setProg] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const [photoBlob, setPhotoBlob] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get(`/lms/programs/${program_id}`),
        api.get("/lms/categories"),
      ]);
      setProg(p.data);
      setCategories(c.data || []);
      if (p.data?.photo_url) {
        try {
          const ph = await api.get(`/lms/programs/${program_id}/photo`);
          setPhotoBlob(`data:${ph.data.mime};base64,${ph.data.data_b64}`);
        } catch {}
      }
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  // eslint-disable-next-line
  }, [program_id]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [program_id]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...prog };
      delete payload._id;
      delete payload.program_id;
      delete payload.code_interne;
      delete payload.variants_count;
      delete payload.tenant_id;
      delete payload.created_at;
      delete payload.created_by;
      delete payload.photo_url;
      await api.put(`/lms/programs/${program_id}`, payload);
      toast.success("Programme enregistre");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const uploadPhoto = async (file) => {
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/lms/programs/${program_id}/upload-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploadee");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const set = (k, v) => setProg(p => ({ ...p, [k]: v }));
  const sectionField = (k) => `section_${k}`;
  const toggleSection = (k) => setOpenSections(s => ({ ...s, [k]: !s[k] }));

  if (loading) return <div className="p-8"><Loader2 className="w-8 h-8 animate-spin text-[#f7c935] mx-auto mt-20" /></div>;
  if (!prog) return <div className="p-8 text-slate-400">Programme non trouve</div>;

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="program-edit-page">
      <PageHeader
        icon={<Pencil />}
        title={prog.title}
        description="Modifier le contenu du programme"
        breadcrumb={[{ label: "Formation" }, { label: "Programmes" }, { label: prog.title }, { label: "Modifier" }]}
        testId="program-edit-header"
        actions={(
          <Button onClick={() => navigate(`/admin/formation/bibliotheque/${program_id}`)} variant="ghost" className="text-slate-400 hover:bg-white/5" data-testid="back-detail-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Retour au détail
          </Button>
        )}
      />

      {/* Top actions */}
      <div className="flex gap-2 mb-6 sticky top-0 bg-[#0b0811] py-2 z-10">
        <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#14101c] font-bold hover:bg-[#ffd948]" data-testid="save-program-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer
        </Button>
      </div>

      {/* Top fixed fields */}
      <div className="bg-[#14101c] border border-white/10 rounded-2xl p-6 mb-6 space-y-4">
        <div className="grid md:grid-cols-[1fr_180px] gap-4">
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Titre du programme</label>
              <Input value={prog.title || ""} onChange={e => set("title", e.target.value)} className={inputCls} data-testid="edit-title" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Sous-titre</label>
              <Input value={prog.sous_titre || ""} onChange={e => set("sous_titre", e.target.value)} className={inputCls} data-testid="edit-sous-titre" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Photo du programme</label>
            <div className="relative aspect-square bg-[#0b0811] border-2 border-dashed border-white/10 rounded-lg overflow-hidden flex items-center justify-center group">
              {photoBlob ? (
                <img src={photoBlob} alt="programme" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="w-8 h-8 text-slate-600" />
              )}
              <label className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex items-center justify-center">
                <Upload className="w-5 h-5 text-white" />
                <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={e => e.target.files?.[0] && uploadPhoto(e.target.files[0])} data-testid="photo-upload" />
              </label>
            </div>
            <div className="text-[10px] text-slate-500 mt-1">JPG/PNG/WebP, ≤ 500 Ko</div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Code interne</label>
            <Input value={prog.code_interne || ""} disabled className={`${inputCls} opacity-60 font-mono`} />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Categorie</label>
            <select value={prog.category_id || ""} onChange={e => set("category_id", e.target.value)} className={`${inputCls} px-3 py-2 rounded w-full`} data-testid="edit-category">
              <option value="">— Selectionner —</option>
              {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Version</label>
            <Input value={prog.version || ""} onChange={e => set("version", e.target.value)} className={inputCls} data-testid="edit-version" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Date de la version</label>
            <Input type="date" value={prog.version_date || ""} onChange={e => set("version_date", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Bloc de competences</label>
            <Input value={prog.bloc_competences || ""} onChange={e => set("bloc_competences", e.target.value)} placeholder="Ex: RNCP34567" className={inputCls} />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Duree heures</label>
            <Input type="number" value={prog.duration_hours || 0} onChange={e => set("duration_hours", Number(e.target.value))} className={inputCls} data-testid="edit-hours" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Duree jours</label>
            <Input type="number" step="0.5" value={prog.duration_days || 0} onChange={e => set("duration_days", Number(e.target.value))} className={inputCls} />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Couleur pastille</label>
            <div className="flex gap-1 flex-wrap">
              {PROGRAM_COLORS.map(c => (
                <button key={c} onClick={() => set("color", c)} className={`w-6 h-6 rounded ${prog.color === c ? "ring-2 ring-white" : ""}`} style={{ background: c }} data-testid={`color-${c}`} />
              ))}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Prix Entreprise €HT</label>
            <Input type="number" value={prog.prix_entreprise || 0} onChange={e => set("prix_entreprise", Number(e.target.value))} className={inputCls} data-testid="edit-prix-ent" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Prix Particulier €HT</label>
            <Input type="number" value={prog.prix_particulier || 0} onChange={e => set("prix_particulier", Number(e.target.value))} className={inputCls} data-testid="edit-prix-part" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Prix Independant €HT</label>
            <Input type="number" value={prog.prix_independant || 0} onChange={e => set("prix_independant", Number(e.target.value))} className={inputCls} data-testid="edit-prix-indep" />
          </div>
        </div>

        <div className="flex gap-4 flex-wrap text-sm">
          <label className="flex items-center gap-2 cursor-pointer" data-testid="edit-publie">
            <input type="checkbox" checked={!!prog.publie_catalogue} onChange={e => set("publie_catalogue", e.target.checked)} />
            <span>Publier sur le catalogue en ligne</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="edit-cpf">
            <input type="checkbox" checked={!!prog.eligible_cpf} onChange={e => set("eligible_cpf", e.target.checked)} />
            <span>Eligible CPF (Compte Personnel Formation)</span>
          </label>
        </div>
      </div>

      {/* Collapsible sections */}
      <div className="space-y-2">
        {SECTIONS.map(s => {
          const fieldKey = sectionField(s.k);
          const isOpen = openSections[s.k];
          const value = prog[fieldKey];
          return (
            <div key={s.k} className="bg-[#14101c] border border-white/10 rounded-xl overflow-hidden" data-testid={`section-${s.k}`}>
              <button onClick={() => toggleSection(s.k)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-white/5">
                <span className="font-semibold text-white">{s.l}</span>
                {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </button>
              {isOpen && (
                <div className="px-5 py-4 border-t border-white/5 space-y-2">
                  {s.type === "text" && (
                    <Input value={value || ""} onChange={e => set(fieldKey, e.target.value)} className={inputCls} data-testid={`input-${s.k}`} />
                  )}
                  {s.type === "select" && (
                    <select value={value || ""} onChange={e => set(fieldKey, e.target.value)} className={`${inputCls} px-3 py-2 rounded w-full`} data-testid={`input-${s.k}`}>
                      <option value="">—</option>
                      {s.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  )}
                  {s.type === "textarea" && (
                    <Textarea value={value || ""} onChange={e => set(fieldKey, e.target.value)} rows={6} className={inputCls} data-testid={`input-${s.k}`} />
                  )}
                  {s.type === "list" && (
                    <ListEditor value={Array.isArray(value) ? value : []} onChange={v => set(fieldKey, v)} testid={`input-${s.k}`} />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ─── Rattachements (modules + evaluations + docs Qualiopi) ─── */}
      <ProgramLinksEditor programId={program_id} api={api} />

      <div className="sticky bottom-0 bg-[#0b0811] py-3 mt-6 border-t border-white/10">
        <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#14101c] font-bold hover:bg-[#ffd948]" data-testid="save-program-bottom-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Enregistrer
        </Button>
      </div>
    </div>
  );
}

// ─── Program attachments editor ─────────────────────────────
function ProgramLinksEditor({ programId, api }) {
  const [links, setLinks] = useState({ modules: [], lessons: [], evaluations: [], doc_templates: [] });
  const [loading, setLoading] = useState(true);
  const [pools, setPools] = useState({ modules: [], lessons: [], evaluations: [], doc_templates: [] });
  const [adding, setAdding] = useState(null); // "module" | "evaluation" | "doc"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, lessons, evals, docs] = await Promise.all([
        api.get("/lms/programs/" + programId + "/links"),
        api.get("/lms/lessons").catch(() => ({ data: [] })),
        api.get("/evaluations/templates").catch(() => ({ data: [] })),
        api.get("/evaluations/doc-templates"),
      ]);
      setLinks({
        modules: l.data.modules || [],
        lessons: l.data.lessons || [],
        evaluations: l.data.evaluations || [],
        doc_templates: l.data.doc_templates || [],
      });
      setPools({ modules: [], lessons: lessons.data || [], evaluations: evals.data || [], doc_templates: docs.data || [] });
    } catch (e) { toast.error("Chargement des rattachements: " + (e.response?.data?.detail || e.message)); }
    setLoading(false);
  // eslint-disable-next-line
  }, [programId]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [programId]);

  const attach = async (kind, id) => {
    try {
      await api.post("/lms/programs/" + programId + "/attach/" + kind, { id });
      setAdding(null);
      toast.success("Rattache");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const detach = async (kind, id) => {
    try {
      await api.delete("/lms/programs/" + programId + "/attach/" + kind + "/" + id);
      toast.success("Detache");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const SECTIONS_CFG = [
    { k: "lessons", kind: "lesson", l: "Modules de cours", empty: "Aucun module de cours rattache.", pool: pools.lessons, id_key: "lesson_id", label_key: "title", sub: (l) => `${(l.module_ids || []).length} sequence${(l.module_ids || []).length > 1 ? "s" : ""} SCORM${l.duration_min ? ` · ${l.duration_min} min` : ""}${l.status === "published" ? "" : " · brouillon"}` },
    { k: "evaluations", kind: "evaluation", l: "Evaluations", empty: "Aucune evaluation rattachee.", pool: pools.evaluations, id_key: "template_id", label_key: "title", sub: (e) => e.eval_type || "" },
    { k: "doc_templates", kind: "doc", l: "Documents Qualiopi", empty: "Aucun document rattache.", pool: pools.doc_templates, id_key: "template_id", label_key: "title", sub: (d) => d.doc_type || "" },
  ];

  if (loading) return <div className="bg-[#14101c] border border-white/10 rounded-xl p-4 my-4"><Loader2 className="w-4 h-4 animate-spin text-[#f7c935]" /></div>;

  return (
    <div className="bg-[#14101c] border border-[#f7c935]/20 rounded-xl p-5 my-4" data-testid="program-links-section">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#f7c935] font-bold mb-3 flex items-center gap-2">
        ⚡ Rattachements — contenu automatique pour les sessions
      </div>
      <p className="text-xs text-slate-400 mb-4">Ce que vous rattachez ici sera automatiquement propose a chaque nouvelle session crees a partir de ce programme.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {SECTIONS_CFG.map((sec) => {
          const current = links[sec.k] || [];
          const attachedIds = current.map((x) => x[sec.id_key]);
          const available = (sec.pool || []).filter((x) => !attachedIds.includes(x[sec.id_key]));
          return (
            <div key={sec.k} className="bg-[#0b0811] border border-white/5 rounded-xl p-3" data-testid={"links-" + sec.k}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-bold text-white text-sm">{sec.l} <span className="text-slate-500 text-xs">({current.length})</span></div>
                <button onClick={() => setAdding(adding === sec.kind ? null : sec.kind)} className="text-[#f7c935] text-xs hover:underline" data-testid={"links-add-" + sec.kind}>{adding === sec.kind ? "×" : "+ Ajouter"}</button>
              </div>
              {adding === sec.kind && (
                <div className="mb-2 bg-[#14101c] border border-white/10 rounded p-2 max-h-44 overflow-y-auto">
                  {available.length === 0 ? (
                    <div className="text-xs text-slate-500 italic">Aucun element disponible.</div>
                  ) : available.map((x) => (
                    <button key={x[sec.id_key]} onClick={() => attach(sec.kind, x[sec.id_key])} className="w-full text-left px-2 py-1 text-xs text-slate-300 hover:bg-white/5 rounded" data-testid={"links-attach-" + sec.kind + "-" + x[sec.id_key]}>
                      {x[sec.label_key]}
                    </button>
                  ))}
                </div>
              )}
              {current.length === 0 ? (
                <div className="text-xs text-slate-500 italic py-2">{sec.empty}</div>
              ) : (
                <div className="space-y-1">
                  {current.map((it) => (
                    <div key={it[sec.id_key]} className="flex items-center gap-2 bg-white/5 border border-white/5 rounded px-2 py-1.5 text-xs">
                      <span className="flex-1 truncate text-slate-200">{it[sec.label_key]}</span>
                      {sec.sub(it) && <span className="text-[9px] text-slate-500">{sec.sub(it)}</span>}
                      <button onClick={() => detach(sec.kind, it[sec.id_key])} className="text-red-400 hover:text-red-300" data-testid={"links-detach-" + sec.kind + "-" + it[sec.id_key]}><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── List editor (objectives, domaines) ───────────────────
function ListEditor({ value, onChange, testid }) {
  const [txt, setTxt] = useState("");
  const add = () => {
    if (!txt.trim()) return;
    onChange([...(value || []), txt.trim()]);
    setTxt("");
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex gap-2">
        <Input value={txt} onChange={e => setTxt(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), add())} placeholder="Ajouter un item..." className={inputCls} />
        <Button onClick={add} size="sm" className="bg-white/10 hover:bg-white/20"><Plus className="w-4 h-4" /></Button>
      </div>
      {value.length > 0 && (
        <ul className="space-y-1">
          {value.map((it, i) => (
            <li key={i} className="flex items-center gap-2 bg-[#0b0811] rounded px-3 py-2 text-sm">
              <span className="flex-1">{it}</span>
              <button onClick={() => remove(i)} className="text-red-400 hover:text-red-300"><X className="w-3 h-3" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
